import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { OPTION_ROW_BASE_CLASS_NAME } from "@/components/ui/optionRow";
import { Button, cn } from "@paribus/shared-ui";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  ChevronDown,
  Copy,
  GitCompare,
  MoreVertical,
  Pen,
  ToggleRight,
  Trash2,
} from "lucide-react";
import React, { useState } from "react";

import type { ChronologyVersion } from "./types";

const TAG_CLASS_NAMES: Record<string, string> = {
  Current: "bg-info text-info-foreground",
  "AI Draft": "bg-ai-subtle-strong text-ai-text",
};

const VersionTags: React.FC<{ version: ChronologyVersion }> = ({ version }) => {
  const tags: string[] = [];
  if (version.is_current) tags.push("Current");
  if (version.tags?.includes("AI Draft")) tags.push("AI Draft");
  if (tags.length === 0) return null;
  return (
    <div className="flex shrink-0 gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className={cn(
            "rounded-md px-2 py-0.5 text-2xs font-semibold uppercase tracking-micro shadow-sm",
            TAG_CLASS_NAMES[tag] ?? "bg-neutral-500 text-text-on-brand"
          )}
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

const selectItemClassName = `${OPTION_ROW_BASE_CLASS_NAME} data-[highlighted]:bg-info-subtle data-[highlighted]:font-bold data-[highlighted]:text-info-text data-[state=checked]:bg-info-subtle data-[state=checked]:font-bold data-[state=checked]:text-info-text`;

export interface VersionDropdownProps {
  versions: ChronologyVersion[];
  selectedVersionId: string | null;
  currentVersion: ChronologyVersion | null;
  isLoading: boolean;
  onVersionChange: (versionId: string) => void;
  onEditName: (versionName: string) => void;
  onSetCurrent: () => void;
  onDuplicate: (versionId: string) => void;
  onDelete: (versionId: string) => void;
  onCompare: () => void;
  canCompare: boolean;
  /** Drop the "Version" label so the identifying name/date gets the width instead of truncating to "V…". */
  compact?: boolean;
}

export const VersionDropdown: React.FC<VersionDropdownProps> = ({
  versions,
  selectedVersionId,
  currentVersion,
  isLoading,
  onVersionChange,
  onEditName,
  onSetCurrent,
  onDuplicate,
  onDelete,
  onCompare,
  canCompare,
  compact = false,
}) => {
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectedVersion = versions.find((v) => v.version_id === selectedVersionId) ?? null;
  const isCurrentVersionSelected = currentVersion?.version_id === selectedVersionId;

  return (
    <div className="flex w-full items-center gap-1.5">
      <SelectPrimitive.Root
        value={selectedVersionId ?? ""}
        onValueChange={onVersionChange}
        disabled={isLoading}
      >
        {/* The whole field shell is the trigger, so the popover anchors to it and
            --radix-select-trigger-width is the shell's width, not the value's. */}
        <SelectPrimitive.Trigger
          aria-label="Version"
          className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-xl border border-neutral-200/80 bg-neutral-50/50 px-3 text-xs font-bold text-neutral-900 outline-none transition-colors hover:bg-neutral-100/50 focus-visible:border-primary focus-visible:shadow-focus-inset disabled:cursor-not-allowed disabled:opacity-50"
        >
          {!compact && (
            <span
              aria-hidden
              className="shrink-0 text-2xs font-semibold uppercase tracking-micro text-neutral-500"
            >
              Version
            </span>
          )}
          <SelectPrimitive.Value
            className="min-w-0 flex-1 text-left"
            placeholder={<span className="text-neutral-400">Select version</span>}
          >
            {selectedVersion ? (
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate">{selectedVersion.displayName}</span>
                <VersionTags version={selectedVersion} />
              </div>
            ) : (
              <span className="text-neutral-400">Select version</span>
            )}
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon asChild>
            <ChevronDown size={14} className="ml-auto shrink-0 text-neutral-400" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            align="start"
            sideOffset={4}
            className="z-popover max-h-[300px] w-[--radix-select-trigger-width] overflow-y-auto rounded-xl border bg-popover p-1 text-popover-foreground shadow-md"
          >
            <SelectPrimitive.Viewport>
              {versions.map((version) => (
                <SelectPrimitive.Item
                  key={version.version_id}
                  value={version.version_id}
                  className={selectItemClassName}
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <SelectPrimitive.ItemText>
                      <span className="truncate">{version.displayName}</span>
                    </SelectPrimitive.ItemText>
                    <VersionTags version={version} />
                  </div>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {selectedVersionId && (
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Version actions"
              disabled={isLoading}
              className="h-10 w-10 shrink-0 rounded-xl border border-neutral-200 bg-card p-0 text-neutral-500 shadow-sm hover:bg-neutral-50 hover:text-neutral-700"
            >
              <MoreVertical size={18} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="min-w-[180px] rounded-xl border-border/50 p-1 shadow-lg"
          >
            <DropdownMenuItem
              onSelect={() => onEditName(selectedVersion?.version_name ?? "")}
              className="gap-2.5 py-2.5 text-dense font-semibold"
            >
              <Pen size={16} />
              <span>Edit Name</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onSetCurrent}
              disabled={isCurrentVersionSelected}
              className="gap-2.5 py-2.5 text-dense font-semibold"
            >
              <ToggleRight size={16} />
              <span>Set as Current</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDuplicate(selectedVersionId)}
              className="gap-2.5 py-2.5 text-dense font-semibold"
            >
              <Copy size={16} />
              <span>Duplicate</span>
            </DropdownMenuItem>
            {canCompare && (
              <DropdownMenuItem
                onSelect={onCompare}
                className="gap-2.5 py-2.5 text-dense font-semibold"
              >
                <GitCompare size={16} />
                <span>Compare versions</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(event) => {
                // Suppress the auto-close so the menu's focus-return doesn't race the
                // confirm dialog's focus trap; close it explicitly instead.
                event.preventDefault();
                setIsMenuOpen(false);
                setIsDeleteConfirmOpen(true);
              }}
              disabled={isCurrentVersionSelected}
              className="gap-2.5 py-2.5 text-dense font-semibold text-destructive-text focus:bg-destructive-subtle focus:text-destructive-text"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <ConfirmDialog
        open={isDeleteConfirmOpen}
        onOpenChange={setIsDeleteConfirmOpen}
        variant="destructive"
        icon={Trash2}
        title="Delete version"
        description="Are you sure you want to delete this version? This action cannot be undone."
        confirmLabel="Delete"
        confirmIcon={Trash2}
        onConfirm={() => {
          if (selectedVersionId) onDelete(selectedVersionId);
          setIsDeleteConfirmOpen(false);
        }}
      />
    </div>
  );
};
